import Player from './Player/Player';
import SceneManager from './Scene/SceneManager';
import LocaleHandler from './Utility/LocaleManager';
import ResourceManager from './Utility/Resource/ResourceManager';
import Ticker, { TickerEvent } from './Utility/Ticker';
import Environment from './Utility/Environment';
import StateManager from './State/StateManager';
import StyleHandler from './Utility/StyleManager';
import SDKManager from './Utility/SDK/SDKManager';
import PreloadScene from './Scene/PreloadScene';
import WorldScene from './Scene/WorldScene';
import LevelScene from './Scene/LevelScene';
import LuckyTurnipsScene from './Scene/LuckyTurnipsScene';
import { GameActions } from './State/Game/GameActions';
import TransitionScene from './Scene/TransitionScene';
import { IGameSettings, SETTINGS } from './Settings';
import { enableTracking } from './Utility/Tracking/Tracking';
import RenderSystem from './System/Render/RenderSystem';
import { WorldDataHelper } from './Utility/WorldDataHelper';
import { PlayerActions } from './State/Player/PlayerActions';
import { IGameState } from './State/Game/IGameState';
import {
    audioSceneId,
    farmTransitionSceneId,
    GameScene,
    plinkoTransitionSceneId,
    preloadSceneId,
    transitionSceneId,
} from './State/Game/GameScene';
import MyPetMiniGameScene from './Scene/MyPetMiniGameScene';
import { ProgressStateHelper } from './State/Player/Progress/ProgressStateHelper';
import { Native } from '@sgorg/mobile-dev-tools';
import { createGameEmitter } from 'Utility/GameEvent/CreateGameEmitter';
import { initTracking } from 'Utility/Tracking/InitTracking';
import { IGameConfig } from 'Utility/IGameConfig';
import { Platform } from 'Utility/Platform';
import { CheatsToggle } from './Utility/CheatsGui/CheatsToggle';
import FarmScene from 'Scene/FarmScene';
import AudioScene from 'Scene/AudioScene';
import FarmTransitionScene from 'Scene/FarmTransitionScene';
import PlinkoTransitionScene from 'Scene/PlinkoTransitionScene';
import PlinkoScene from 'Scene/PlinkoScene';
import { checkWebpSupport } from 'Utility/WebPFunctions';
import { PlayerStateHelper } from 'State/Player/PlayerStateHelper';
import { AvoTracking } from 'Utility/Tracking/Avo/AvoTracking';
import { FARM_UNLOCK_LEVEL } from 'Constants/Farm';
import { questUnlockLevel } from 'Constants/Quests';
import { FTUXType } from 'Player/PlayerProperty/Progress/FTUXType';
import { AnyBoosterType, PreBoosterType } from 'State/Card/BoosterType';
import { FTUXStateHelper } from 'State/Game/FTUX/FTUXStateHelper';
import { FieldStateHelper } from 'State/Player/Fields/FieldStateHelper';
import { IPlayerState } from 'State/Player/IPlayerState';
import { LevelType, IPlayerLevelData } from 'State/Player/Progress/PlayerLevels';
import { StarChestStateHelper } from 'State/Player/StarChests/StarChestStateHelper';
import { getUnlockedFields } from 'System/Field/FieldHelper';
import { unlockLevelByPreBoosterType } from 'System/UI/Menu/LevelPopup/LevelPopupBooster';
import { ILimit, MathHelper } from 'Utility/MathHelper';

export default class Game {
    public static player: Player;
    public static scene: SceneManager;
    public static resource: ResourceManager;
    public static store: StateManager;
    public static locale: LocaleHandler;
    public static style: StyleHandler;
    public static config: IGameConfig;
    public static ticker: Ticker;
    public static sdk: SDKManager;
    public static settings: IGameSettings;
    public static serverTimeOffset: number = 0;

    public static giftCallbackSet: boolean = false;
    public static isFrozen = false;

    public static async init(config: IGameConfig) {
        Game.config = config;
        Game.store = new StateManager();
        Game.scene = new SceneManager();
        Game.locale = new LocaleHandler();
        Game.style = new StyleHandler();
        Game.resource = new ResourceManager();
        Game.player = new Player();
        Game.ticker = new Ticker();
        Game.settings = SETTINGS;

        Game.sdk = new SDKManager();
        await Game.sdk.initialized;

        // enableTracking();

        // Initialize Avo.
        // AvoTracking.init();
        // Create global game event emitter for "external" game modules e.g. local notifications.
        createGameEmitter();
        // Initialize tracking handlers.
        // initTracking();

        Game.ticker.fpsLimit = Game.config.fpsLimit;

        Game.locale.setLanguage(config.language);
        Game.ticker.start();
        Game.store.addListener({
            selector: (state: IGameState) => {
                return state;
            },
            callback: Game.handleStateChanged,
        });

        // Should be awaited before resources are requested.
        await checkWebpSupport();

        RenderSystem.init();

        // Start update loop.
        Game.ticker.on(TickerEvent.TICK, this.scene.update.bind(this.scene));

        Game.scene.addScene(GameScene.LEVEL, new LevelScene());
        Game.scene.addScene(GameScene.WORLD, new WorldScene());
        Game.scene.addScene(transitionSceneId, new TransitionScene(1));
        Game.scene.addScene(GameScene.LUCKY_TURNIPS, new LuckyTurnipsScene());
        Game.scene.addScene(GameScene.FARM, new FarmScene());
        Game.scene.addScene(farmTransitionSceneId, new FarmTransitionScene(1));
        Game.scene.addScene(GameScene.PLINKO, new PlinkoScene());
        Game.scene.addScene(plinkoTransitionSceneId, new PlinkoTransitionScene(1));
        Game.scene.addScene(preloadSceneId, new PreloadScene());

        Game.scene.addScene(GameScene.MY_PET_MINI_GAME, new MyPetMiniGameScene());
        Game.scene.addScene(audioSceneId, new AudioScene());

        await Game.scene.startScene(preloadSceneId);
        await Game.scene.startScene(audioSceneId, {}, true);

        Native.main.closeSplashScreen();
        new CheatsToggle();
    }

    /**
     * Called after initial preload is complete.
     */
    public static start(): void {
        if ([Platform.FACEBOOK, Platform.UNKNOWN].includes(Game.config.platform)) {
            document.getElementById('canvas').style.background = 'none';
        }

        if (process?.env.NODE_ENV === Environment.DEVELOPMENT && this.debugLoadSpecificLevelIfRequested()) {
            // nothing to do
        } else if (ProgressStateHelper.getLevelStars(Game.store.getState(), 1) === 0) {
            // Load first level.
            GameActions.loadLevel({ levelNumber: 1, preBoosters: [] });
        } else {
            this.unlockNextWorldIfPossible();

            if (this.isLuckyTurnipsInProgress()) {
                GameActions.loadLuckyTurnips();
            } else {
                // Load active world.
                GameActions.loadWorld({
                    clearUI: false,
                    firstLoad: true,
                });
            }
        }

        Game.sdk.onStart();
    }

    private static debugLoadSpecificLevelIfRequested() {
        const debugLevelNumber = Number(window.location.hash.slice(1));
        if (debugLevelNumber > 0) {
            GameActions.loadLevel({
                levelNumber: debugLevelNumber,
                preBoosters: [],
            });
            return true;
        }
        return false;
    }

    private static unlockNextWorldIfPossible() {
        const state = Game.store.getState();

        const atMaxWorldLevel =
            Game.player.getHighestLevelCompleted(state) >= WorldDataHelper.maxLevel(state.playerState.maxWorld);

        const nextMapExists = Boolean(WorldDataHelper.getWorldData(state.playerState.maxWorld + 1));
        if (atMaxWorldLevel && nextMapExists) {
            PlayerActions.setPlayerState(PlayerStateHelper.unlockAllPossibleWorlds(state).playerState);
        }
    }

    private static isLuckyTurnipsInProgress() {
        return Game.store.getState().playerState.luckyTurnipsState !== null;
    }

    public static handleStateChanged(state: IGameState) {
        // Check if we can start the game.
        if (!Game.store.getPreviousState().gameLoaded && state.gameLoaded) {
            Game.start();
        }

        Game.player.handleStateChanged(state);
        Game.scene.handleStateChanged(state);
    }
}
